import { Check, mergeClassName, ProgressSteps } from '@chain-zen/ui'
import React from 'react'

import { useCarrierOnboarding } from '../_context/carrier-onboarding-context'
import type { TCarrierOnboardingStepID } from '../_types'

/**
 * The onboarding steps for a carrier
 */
export interface IVerificationStep {
  id: string
  label: string
}

interface CarrierVerificationChecklistProps {
  currentStep: number
  steps: IVerificationStep[]
}

/**
 * Displays a static verification checklist for carrier onboarding.
 * Collapsible functionality has been removed for simplicity.
 */
export const CarrierVerificationChecklist: React.FC<CarrierVerificationChecklistProps> = ({ steps, currentStep }) => {
  const { isStepCompleted } = useCarrierOnboarding()

  return (
    <div className="w-full space-y-6">
      {/* Verification Checklist */}
      <div className="w-full p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Verification Checklist</h3>
          </div>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={mergeClassName(
                  'p-3 border rounded-md transition-colors duration-200',
                  currentStep === index
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 bg-transparent'
                )}
              >
                <div className="flex items-center gap-3">
                  {isStepCompleted(step.id as TCarrierOnboardingStepID) ? (
                    <div className="text-green-500">
                      <Check className="h-4 w-4" />
                    </div>
                  ) : (
                    <div
                      className={mergeClassName(
                        'w-4 h-4 rounded-full border transition-colors duration-200',
                        currentStep === index ? 'border-blue-500' : 'border-gray-300 dark:border-gray-500'
                      )}
                    />
                  )}
                  <span className="font-medium text-sm">{step.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="w-full pt-2 pb-4 flex justify-center items-center">
        <ProgressSteps numberOfSteps={steps.length} activeStep={currentStep + 1} />
      </div>
    </div>
  )
}
